(() => {
  'use strict';
  const plugs = ['US', 'UK', 'EU'];
  const packages = ['A machine (including a battery)', 'One machine (including one battery) + 1 extra battery'];
  const labels = ['Concentrator + 1 battery', 'Concentrator + 2 batteries'];
  const instances = new WeakMap();

  function parseOptions(raw) {
    try {
      const data = JSON.parse(raw);
      if (!data || data.productId !== '10130859917627' || data.handle !== 'oxygen-concentrator-zy-p-01'
        || !Array.isArray(data.options) || data.options.length !== 2
        || data.options[0] !== 'Plug' || data.options[1] !== 'Combination Set'
        || !Array.isArray(data.variants) || data.variants.length !== 6) return null;
      const ids = new Set(), pairs = new Set();
      for (const variant of data.variants) {
        if (!variant || typeof variant.id !== 'string' || !/^[1-9]\d*$/.test(variant.id)
          || ids.has(variant.id) || !plugs.includes(variant.plug) || !packages.includes(variant.package)
          || typeof variant.available !== 'boolean') return null;
        const pair = `${variant.plug}:${packages.indexOf(variant.package)}`;
        if (pairs.has(pair)) return null;
        ids.add(variant.id); pairs.add(pair);
      }
      return data.variants.map(({ id, plug, package: batteryPackage, available }) => ({ id, plug, package: batteryPackage, available }));
    } catch (_) { return null; }
  }

  function initRoot(root, view) {
    if (instances.has(root)) return instances.get(root);
    // Never substitute for a failed/missing native price and purchase controller.
    if (typeof view?.__ziqingProductController?.init !== 'function') return null;
    const form = root.closest('form');
    const productRoot = root.closest('[data-zp-root]');
    if (!form || !productRoot || form.closest('[data-zp-root]') !== productRoot) return null;
    const controls = [...form.querySelectorAll('select[name="id"][data-zp-variant]')].filter((node) => node.closest('form') === form);
    if (controls.length !== 1) return null;
    const select = controls[0];
    if (select.disabled || select.hidden || !select.id || select.multiple) return null;
    const nativeLabels = [...form.querySelectorAll('label')].filter((label) => label.htmlFor === select.id);
    if (nativeLabels.length !== 1 || nativeLabels[0].hidden) return null;
    const variants = parseOptions(root.querySelector('[data-zso-data]')?.textContent || '');
    if (!variants) return null;
    const validNativeOptions = () => {
      const ids = [...select.options].map((option) => String(option.value));
      return ids.length === variants.length && new Set(ids).size === ids.length
        && variants.every((variant) => ids.includes(variant.id));
    };
    if (!validNativeOptions()) return null;
    const plugButtons = [...root.querySelectorAll('[data-zso-plug]')];
    const packageButtons = [...root.querySelectorAll('[data-zso-package]')];
    const status = root.querySelector('[data-zso-status]');
    const validButtons = (buttons, key, values) => buttons.length === values.length && buttons.every((button, index) =>
      button.tagName === 'BUTTON' && button.type === 'button' && !button.name
      && button.dataset[key] === values[index] && button.querySelector('[data-zso-availability]'));
    if (!status || !validButtons(plugButtons, 'zsoPlug', plugs)
      || !validButtons(packageButtons, 'zsoPackage', ['0', '1'])) return null;

    let stopped = false, pending = null;
    const removers = [];
    const listen = (target, type, handler) => {
      target.addEventListener(type, handler);
      removers.push(() => target.removeEventListener(type, handler));
    };
    const hiddenNodes = [select, ...nativeLabels, ...[...form.querySelectorAll('.zp-selection')].filter((node) => node.querySelector('[data-zp-selected-option]'))];
    const priorVisibility = hiddenNodes.map((node) => [node, node.hidden]);
    const current = () => variants.find((variant) => variant.id === String(select.value));
    const combination = (plug, packageIndex) => variants.find((variant) => variant.plug === plug && variant.package === packages[packageIndex]);
    const controller = {
      sync,
      destroy(focusFallback = false) {
        if (stopped) return;
        stopped = true;
        removers.forEach((remove) => remove());
        if (pending !== null) view.cancelAnimationFrame(pending);
        priorVisibility.forEach(([node, hidden]) => { node.hidden = hidden; });
        root.hidden = true; root.removeAttribute('data-zso-ready');
        instances.delete(root);
        if (focusFallback && root.contains(root.ownerDocument.activeElement) && select.isConnected) select.focus({ preventScroll: true });
      },
    };
    function sync() {
      if (stopped) return false;
      const selected = current();
      if (!selected || select.disabled || !validNativeOptions()
        || !validButtons(plugButtons, 'zsoPlug', plugs)
        || !validButtons(packageButtons, 'zsoPackage', ['0', '1'])) { controller.destroy(true); return false; }
      const selectedPackage = packages.indexOf(selected.package);
      const paint = (button, variant, pressed) => {
        button.setAttribute('aria-pressed', String(pressed));
        const availability = button.querySelector('[data-zso-availability]');
        availability.hidden = variant.available;
        availability.textContent = variant.available ? '' : 'Sold out';
      };
      plugButtons.forEach((button) => paint(button, combination(button.dataset.zsoPlug, selectedPackage), button.dataset.zsoPlug === selected.plug));
      packageButtons.forEach((button) => paint(button, combination(selected.plug, Number(button.dataset.zsoPackage)), Number(button.dataset.zsoPackage) === selectedPackage));
      status.textContent = `${selected.plug} · ${labels[selectedPackage]}${selected.available ? '' : ' · Sold out'}`;
      return true;
    }
    const choose = (plug, packageIndex) => {
      if (stopped || !sync()) return;
      const next = combination(plug, packageIndex);
      if (!next || next.id === String(select.value)) return;
      select.value = next.id;
      if (String(select.value) !== next.id) { controller.destroy(true); return; }
      // This is the sole write: the existing product controller owns price,
      // availability, media, URL and the original native Shopify submission.
      select.dispatchEvent(new view.Event('change', { bubbles: true }));
      sync();
    };
    if (!sync()) return null;
    plugButtons.forEach((button) => listen(button, 'click', () => {
      const selected = current();
      if (selected) choose(button.dataset.zsoPlug, packages.indexOf(selected.package));
      else controller.destroy(true);
    }));
    packageButtons.forEach((button) => listen(button, 'click', () => {
      const selected = current();
      if (selected) choose(selected.plug, Number(button.dataset.zsoPackage));
      else controller.destroy(true);
    }));
    listen(select, 'change', sync);
    const afterNativeRestore = () => {
      if (pending !== null) view.cancelAnimationFrame(pending);
      pending = view.requestAnimationFrame(() => { pending = null; sync(); });
    };
    // The browser may restore form values after popstate/pageshow dispatch.
    // Reconcile from the native select; never dispatch change or edit history here.
    listen(view, 'pageshow', afterNativeRestore);
    listen(view, 'popstate', afterNativeRestore);
    listen(form, 'reset', afterNativeRestore);
    hiddenNodes.forEach((node) => { node.hidden = true; });
    root.hidden = false; root.setAttribute('data-zso-ready', 'true');
    instances.set(root, controller);
    return controller;
  }

  const rootsWithin = (scope) => [
    ...(scope.matches?.('[data-zso-root]') ? [scope] : []), ...scope.querySelectorAll('[data-zso-root]'),
  ];
  function install(view, document) {
    if (view.__ziqingSJOptions) { view.__ziqingSJOptions.init(document); return view.__ziqingSJOptions; }
    const init = (scope) => rootsWithin(scope).forEach((root) => initRoot(root, view));
    const api = { init };
    view.__ziqingSJOptions = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
    else init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy()));
    return api;
  }
  if (typeof window === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = { parseOptions, initRoot, install };
  } else install(window, document);
})();
